//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';
import styled from 'styled-components';

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { getCompatibilityErrorMessage } from '@campaigns/components/shared/prepaid-purchase-options/PrepaidPurchaseOptionsCompatibilityUtils';
import { PrepaidPurchaseOptionsTableRow } from '@campaigns/components/shared/prepaid-purchase-options/prepaid-purchase-options-table-row/PrepaidPurchaseOptionsTableRow';
import { usePurchaseOptionsContext } from '@campaigns/components/shared/purchase-options/PurchaseOptionsContext';
import {
  PurchaseOptionsSortKey,
  WIDTH_CHECKBOX,
  WIDTH_EXPIRATION_DATE,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { Alert } from '@components/atoms/alert/Alert';
import { BodyText, TableHeaderCellText } from '@components/atoms/text/Text';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import { Table } from '@components/organisms/table/Table';

const TABLE_CAPTION_ID = 'sampling-campaigns-purchase-options-table-caption';

const EMPTY_SELECTED_OPTION_IDS: string[] = [];

interface PrepaidPurchaseOptionsTableProps {
  allSelectedOptions?: PurchaseOption[];
  onRequestCampaign?: (purchaseOption: PurchaseOption) => void;
  onSelectionChange?: (optionIds: string[]) => void;
  onSortChange: (sortKey: PurchaseOptionsSortKey) => void;
  purchaseOptions: PurchaseOption[];
  selectedOptionIds?: string[];
  showRequestCampaignButton?: boolean;
  sortDir: SortDir;
  sortKey: PurchaseOptionsSortKey;
}

export const PrepaidPurchaseOptionsTable = ({
  allSelectedOptions,
  onRequestCampaign,
  onSelectionChange,
  onSortChange,
  purchaseOptions,
  selectedOptionIds = EMPTY_SELECTED_OPTION_IDS,
  showRequestCampaignButton = false,
  sortDir,
  sortKey,
}: PrepaidPurchaseOptionsTableProps) => {
  const { readOnly } = usePurchaseOptionsContext();

  const handleToggle = (opportunityId: string) => {
    const next = selectedOptionIds.includes(opportunityId)
      ? selectedOptionIds.filter((id) => id !== opportunityId)
      : [...selectedOptionIds, opportunityId];
    onSelectionChange?.(next);
  };

  if (purchaseOptions.length === 0) {
    return (
      <EmptyText>
        No options matched your search criteria. Please refine your search and
        try again.
      </EmptyText>
    );
  }

  const iconName: UnisonIconType =
    sortDir === SortDir.ASC ? 'chevron-up' : 'chevron-down';

  const compatibilityError = allSelectedOptions
    ? getCompatibilityErrorMessage(allSelectedOptions)
    : undefined;

  const headerCells = [
    <TableHeaderCellText justifyContent='flex-start' key='purchase'>
      PURCHASE
    </TableHeaderCellText>,
    <TableHeaderCellText justifyContent='flex-start' key='content'>
      CONTENT
    </TableHeaderCellText>,
    <TableHeaderCellText justifyContent='flex-start' key='deliveryMethod'>
      DELIVERY METHOD
    </TableHeaderCellText>,
    <TableHeaderCellText justifyContent='flex-start' key='usage'>
      USAGE
    </TableHeaderCellText>,
    <InvisibleButton
      key='expiration'
      onClick={() => {
        onSortChange(PurchaseOptionsSortKey.EXPIRY_DATE);
      }}
      trackingId={
        SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_SORT_BY_EXPIRATION_DATE_BUTTON
      }
    >
      <ExpirationDateCell justifyContent='flex-start'>
        EXPIRY
        <VisuallyHiddenText>
          Sort by expiry&nbsp;
          {sortDir === SortDir.ASC ? 'ascending' : 'descending'}
        </VisuallyHiddenText>
        {sortKey === PurchaseOptionsSortKey.EXPIRY_DATE && (
          <Icon name={iconName} />
        )}
      </ExpirationDateCell>
    </InvisibleButton>,
  ];

  if (showRequestCampaignButton) {
    headerCells.push(
      <TableHeaderCellText justifyContent='flex-start' key='actions'>
        ACTIONS
      </TableHeaderCellText>,
    );
  }

  if (!readOnly) {
    headerCells.unshift(
      <CheckboxCell justifyContent='flex-start' key='selected' />,
    );
  }

  return (
    <div>
      {compatibilityError && (
        <StyledAlert type='error'>{compatibilityError}</StyledAlert>
      )}
      <Table
        body={purchaseOptions.map((purchaseOption) => (
          <PrepaidPurchaseOptionsTableRow
            isChecked={selectedOptionIds.includes(purchaseOption.opportunityId)}
            key={purchaseOption.opportunityId}
            onRequestCampaign={onRequestCampaign}
            onToggle={() => handleToggle(purchaseOption.opportunityId)}
            purchaseOption={purchaseOption}
            showRequestCampaignButton={showRequestCampaignButton}
          />
        ))}
        caption={<VisuallyHiddenText>Purchase Options</VisuallyHiddenText>}
        captionId={TABLE_CAPTION_ID}
        headerCells={headerCells}
      />
    </div>
  );
};

const StyledTableHeaderCellText = styled(TableHeaderCellText)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const CheckboxCell = styled(TableHeaderCellText)`
  ${WIDTH_CHECKBOX}
`;

const ExpirationDateCell = styled(StyledTableHeaderCellText)`
  ${WIDTH_EXPIRATION_DATE}
`;

const EmptyText = styled(BodyText)(
  ({ theme }) => `
    margin-top: ${theme.spacings[2]};
  `,
);

const StyledAlert = styled(Alert)`
  margin-bottom: ${({ theme }) => theme.spacings[2]};
`;
